/*
  Admin page — small presentational pieces shared by the tabs (cards, setting rows, role pill, avatar,
  callout). Tokens only (ink / line / surface / brand / status), so every theme works.
*/
import { forwardRef } from 'react';
import { Lock, Search as SearchIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toneClass } from '@/lib/status';
import { SUPER_ADMIN_ROLE } from '@/lib/permissions';
import { avatarFill, userInitials } from '@/components/admin/adminModel';

/** Card with a header (title + one muted line, optional right side) — Settings sections, Integrations. */
export function AdminCard({ title, subtitle, icon: Icon, iconTone = 'navy', badge, aside, children, className, bodyClassName, ...props }) {
  return (
    <section className={cn('rounded-card border border-line bg-surface shadow-card', className)} {...props}>
      {title ? (
        <header className="flex flex-wrap items-start gap-x-4 gap-y-2 border-b border-line-2 px-5 py-4">
          {Icon ? (
            <span className={cn('ui-icon-tile', toneClass(iconTone))} aria-hidden="true">
              <Icon className="h-[18px] w-[18px]" />
            </span>
          ) : null}
          <div className="min-w-[min(220px,100%)] flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[15px] font-bold leading-6 text-ink">{title}</h2>
              {badge}
            </div>
            {subtitle ? <p className="mt-0.5 text-[12.5px] leading-snug text-ink-3">{subtitle}</p> : null}
          </div>
          {aside ? <div className="flex shrink-0 items-center gap-2 text-[12.5px] text-ink-3">{aside}</div> : null}
        </header>
      ) : null}
      <div className={cn('px-5 py-4', bodyClassName)}>{children}</div>
    </section>
  );
}

/**
 * One setting: name + hint on the left, the control on the right — side by side only from xl (1280px), where
 * the card (beside the 248px sidebar) still leaves the control 600px; narrower windows and phones stack them,
 * so inputs and theme cards are never squeezed. The label column gives way (200–280px) before the control does.
 * scroll-mb: a focused field scrolls clear of the sticky unsaved bar.
 */
export function SettingRow({ label, hint, htmlFor, children, className }) {
  return (
    <div className={cn('grid gap-x-10 gap-y-2 py-4 first:pt-1 last:pb-1 xl:grid-cols-[minmax(200px,280px)_minmax(0,1fr)]', className)}>
      <div className="min-w-0">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="text-[13.5px] font-bold text-ink">{label}</label>
        ) : (
          <p className="text-[13.5px] font-bold text-ink">{label}</p>
        )}
        {hint ? <p className="mt-0.5 max-w-[560px] text-[12.5px] leading-snug text-ink-3 xl:max-w-[280px]">{hint}</p> : null}
      </div>
      <div className="min-w-0 scroll-mb-32" data-setting-control="">{children}</div>
    </div>
  );
}

/** Role as text, not a rainbow: Super Admin solid navy + lock, Admin soft navy, everyone else gray. */
export const RolePill = forwardRef(({ role, className, children, ...props }, ref) => {
  const isSuper = role === SUPER_ADMIN_ROLE;
  const isAdmin = role === 'Admin';
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex h-[22px] max-w-full items-center gap-1 whitespace-nowrap rounded-full px-2 text-[12px] font-semibold',
        isSuper && 'bg-brand text-brand-fg',
        isAdmin && 'bg-brand-soft text-brand-ink',
        !isSuper && !isAdmin && 'bg-[color:var(--status-gray-bg)] text-ink-2',
        className
      )}
      data-role={role}
      {...props}
    >
      {isSuper ? <Lock className="h-[11px] w-[11px]" aria-hidden="true" /> : null}
      <span className="truncate">{role}</span>
      {children}
    </span>
  );
});
RolePill.displayName = 'RolePill';

export function UserAvatar({ name, seed, size = 28, className }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-grid shrink-0 place-items-center rounded-full font-bold text-white', className)}
      style={{ width: size, height: size, fontSize: size <= 22 ? 9.5 : 11, background: avatarFill(seed || name) }}
    >
      {userInitials(name)}
    </span>
  );
}

const CALLOUT_TONES = {
  amber: 'border-[color:color-mix(in_srgb,var(--status-amber)_38%,transparent)] bg-status-amber-bg',
  navy: 'border-brand-soft-border bg-brand-soft',
  red: 'border-[color:var(--danger-border)] bg-status-red-bg',
};

/**
 * Readable callout: ink text on a tinted ground (never pale amber text), icon tile, actions on the right.
 * Static by default (role="note" — not announced every time a tab opens); pass live="alert" only for a
 * message that appears because of something the user just did.
 */
export function AdminCallout({ tone = 'amber', icon: Icon, title, children, actions, className, live, ...props }) {
  return (
    <div
      role={live || 'note'}
      className={cn('flex flex-wrap items-start gap-x-4 gap-y-3 rounded-card border px-4 py-3.5 sm:px-5', CALLOUT_TONES[tone] || CALLOUT_TONES.navy, className)}
      {...props}
    >
      {Icon ? (
        <span className={cn('ui-icon-tile bg-surface', toneClass(tone))} aria-hidden="true">
          <Icon className="h-[18px] w-[18px]" />
        </span>
      ) : null}
      <div className="min-w-[220px] flex-1 text-[13px] leading-relaxed text-ink-2">
        {title ? <p className="text-[13.5px] font-bold text-ink">{title}</p> : null}
        {children}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2 self-center">{actions}</div> : null}
    </div>
  );
}

/** Small uppercase section label inside cards. */
export function UpperLabel({ children, className }) {
  return <p className={cn('text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3', className)}>{children}</p>;
}

/** Table-ish header cell style reused by the admin tables. */
export const thClass = 'h-9 whitespace-nowrap bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3';
export const tdClass = 'px-3 align-middle text-[13px] text-ink';

/** Segmented control (.seg): one choice, counts muted after the label. buttonClassName: e.g. taller on phones. */
export function Segmented({ options, value, onChange, label, testIdPrefix, className, buttonClassName }) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn('inline-flex shrink-0 gap-0.5 rounded-[11px] bg-[color:var(--status-gray-bg)] p-[3px]', className)}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            data-testid={testIdPrefix ? `${testIdPrefix}-${option.value}` : undefined}
            className={cn(
              'inline-flex h-[30px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active ? 'bg-surface text-ink shadow-card' : 'text-ink-2 hover:text-ink',
              buttonClassName
            )}
          >
            {option.label}
            {option.count !== undefined && option.count !== null ? (
              <span className="text-[11.5px] font-semibold tabular-nums text-ink-3">{option.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Search box with the "/" shortcut hint. */
export const SearchBox = forwardRef(({ value, onChange, placeholder, label, className, testId, shortcut = '/' }, ref) => (
  <label className={cn('relative flex h-9 min-w-0 items-center', className)}>
    <span className="sr-only">{label || placeholder}</span>
    <SearchIcon className="pointer-events-none absolute left-3 h-[15px] w-[15px] text-ink-3" aria-hidden="true" />
    <input
      ref={ref}
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className={cn(
        'h-9 w-full min-w-0 rounded-control border border-line bg-surface pl-9 text-[13px] text-ink placeholder:text-ink-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden',
        shortcut ? 'pr-9' : 'pr-3'
      )}
    />
    {shortcut && !value ? <kbd className="ui-kbd pointer-events-none absolute right-2.5" aria-hidden="true">{shortcut}</kbd> : null}
  </label>
));
SearchBox.displayName = 'SearchBox';
